/**
 * ParcelTerrain3D — interactive 3D terrain view of a parcel.
 *
 * Renders the parcel polygon as a WebGL mesh with the top surface
 * displaced by DEM elevation. The user can rotate/zoom/pan with the
 * mouse (OrbitControls). Compared to the prior SVG isometric prism
 * (ParcelCakeView), this:
 *
 *   - lets you actually rotate to see relief from any angle
 *   - tapers side walls to the local elevation along the polygon
 *     boundary (each top vertex's Z is the interpolated grid value,
 *     not a uniform extrusion height)
 *   - renders only the parcel shape, not the bbox rectangle — the
 *     mesh is an ExtrudeGeometry from the polygon, so the silhouette
 *     and shadows match the actual parcel outline
 *
 * Falls back to null when the polygon or elevation grid is missing;
 * the caller is expected to render an alternate (e.g. the previous
 * SVG cake view) in that case.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import Delaunator from 'delaunator';

// --- Bilinear interpolation over the elevation grid -----------------
//
// Grid cells from /elevation-grid are centered at fractional
// positions (col + 0.5) / size and (row + 0.5) / size of the bbox.
// For a polygon vertex at fractional position (fx, fy) in [0..1]^2,
// we map to grid coords (col, row) and bilinearly interpolate the
// four surrounding cell centers, falling back to clamped edges
// outside the central area.
function bilinearElev(elevByRC, gs, fx, fy) {
  const gx = fx * gs - 0.5;
  const gy = fy * gs - 0.5;
  const c0 = Math.max(0, Math.min(gs - 1, Math.floor(gx)));
  const c1 = Math.max(0, Math.min(gs - 1, c0 + 1));
  const r0 = Math.max(0, Math.min(gs - 1, Math.floor(gy)));
  const r1 = Math.max(0, Math.min(gs - 1, r0 + 1));
  const tx = Math.max(0, Math.min(1, gx - c0));
  const ty = Math.max(0, Math.min(1, gy - r0));

  // Pick the nearest non-null neighbour for any NoData cells so the
  // interpolation doesn't get NaN'd by sparse coverage.
  const pick = (r, c) => {
    const v = elevByRC[r][c];
    if (v != null) return v;
    // Spiral out to find a non-null fallback
    for (let radius = 1; radius < gs; radius++) {
      for (let dr = -radius; dr <= radius; dr++) {
        for (let dc = -radius; dc <= radius; dc++) {
          const rr = Math.max(0, Math.min(gs - 1, r + dr));
          const cc = Math.max(0, Math.min(gs - 1, c + dc));
          const w = elevByRC[rr][cc];
          if (w != null) return w;
        }
      }
    }
    return 0;
  };
  const a = pick(r0, c0);
  const b = pick(r0, c1);
  const c = pick(r1, c0);
  const d = pick(r1, c1);
  return (1 - tx) * (1 - ty) * a + tx * (1 - ty) * b + (1 - tx) * ty * c + tx * ty * d;
}

// Build a colour ramp from elevation t in [0..1]: sandy → ochre →
// sienna. Same palette as the SVG cake view, just sampled at a
// finer resolution for the smoother mesh.
function elevColor(t) {
  t = Math.max(0, Math.min(1, t));
  const r0 = 0xd4, g0 = 0xa4, b0 = 0x64;
  const r1 = 0x7a, g1 = 0x3f, b1 = 0x17;
  return new THREE.Color(
    (r0 + (r1 - r0) * t) / 255,
    (g0 + (g1 - g0) * t) / 255,
    (b0 + (b1 - b0) * t) / 255,
  );
}

/**
 * Slow auto-rotation when the user hasn't touched the canvas yet —
 * makes the 3D-ness obvious at a glance. OrbitControls (with
 * autoRotate=true) takes care of the actual rotation; this lets us
 * turn it off as soon as the user interacts.
 */
function AutoSpin({ controlsRef, until }) {
  useFrame(() => {
    const c = controlsRef.current;
    if (!c) return;
    if (!c.userData) c.userData = {};
    const interacted = c.userData.interacted === true;
    if (interacted) {
      c.autoRotate = false;
    } else if (until == null || Date.now() < until) {
      c.autoRotate = true;
    } else {
      c.autoRotate = false;
    }
  });
  return null;
}

// Hand-rolled fit-bounds for the camera given the mesh bbox so the
// model fills the canvas without needing measurement.
//
// The mesh is Z-up (ExtrudeGeometry extrudes the shape's XY outline
// along +Z) so we explicitly orient the camera to match. With
// camera.up = Z and a 3/4-style position (above the model and offset
// to one corner) the user sees a true oblique view immediately —
// previously the default Y-up camera looking from [80,80,80] saw the
// mesh edge-on, which read as "directly above" because the extrusion
// axis happened to align with the camera's right vector.
function fitCamera(camera, controls, box) {
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);
  const horizDim = Math.max(size.x, size.y);
  // With the x10 vertical exaggeration, peaks can now stand ~1.8×
  // the horizontal extent. The camera must pull back to keep them
  // in frame from a 3/4 angle. Use the largest of (horizontal,
  // vertical) as the distance scalar so tall ranges and wide ranches
  // both fit.
  const scale = Math.max(horizDim, size.z);
  camera.up.set(0, 0, 1);
  // Head-on from south, slightly east-of-centre, moderate elevation
  // — matches the framing the owner liked in their reference shot.
  camera.position.set(
    center.x + scale * 0.40,
    center.y - scale * 1.90,
    center.z + scale * 0.95,
  );
  const reach = scale * 4;
  camera.near = reach * 0.005;
  camera.far = reach * 50;
  camera.updateProjectionMatrix();
  if (controls) {
    controls.target.copy(center);
    controls.update();
  }
}

// Builds the THREE geometry: extrude the parcel polygon from depth=0
// to a uniform tall height, then displace the TOP-face vertices to
// the locally-interpolated elevation so the prism has real relief.
// Bottom face stays flat (z=0) and side walls automatically taper
// because the top face vertices vary in Z.
// Ray-casting point-in-polygon test. polygon is an array of [x, y]
// pairs (no closing duplicate vertex required). Returns true if
// (px, py) is strictly inside.
function pointInPolygon(px, py, polygon) {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    const intersect = ((yi > py) !== (yj > py))
      && (px < ((xj - xi) * (py - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function buildTerrainMesh(coords2d, elevByRC, gs, eMin, eMax, vScale) {
  // Switch to mesh-XY coords: y grows NORTH (negate the input's
  // south-positive coords2d frame). The polygon becomes our ground
  // truth for both the top-face triangulation and the side-wall
  // skirt. Drop the trailing duplicate vertex if the polygon is
  // closed by repetition (GeoJSON convention).
  const polyXY = [];
  for (let i = 0; i < coords2d.length; i++) {
    polyXY.push([coords2d[i][0], -coords2d[i][1]]);
  }
  if (polyXY.length > 1) {
    const a = polyXY[0], b = polyXY[polyXY.length - 1];
    if (Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6) {
      polyXY.pop();
    }
  }

  // bbox in mesh-XY
  let mnX = Infinity, mxX = -Infinity, mnY = Infinity, mxY = -Infinity;
  for (const [x, y] of polyXY) {
    if (x < mnX) mnX = x;
    if (x > mxX) mxX = x;
    if (y < mnY) mnY = y;
    if (y > mxY) mxY = y;
  }
  const spanX = mxX - mnX || 1;
  const spanY = mxY - mnY || 1;
  const eRange = Math.max(1, eMax - eMin);
  const zFromElev = (elev) => vScale * Math.max(0, (elev - eMin)) / eRange;

  // ---- Top surface: Delaunay triangulation of polygon outline +
  // interior grid points -----------------------------------------
  //
  // Previous implementation used THREE.ExtrudeGeometry which
  // triangulates the cap from the polygon outline alone — the
  // interior elevation grid never became mesh vertices and the
  // surface flat-shaded between outline edges, giving a 240-acre
  // parcel only ~16 triangles on top. Now we sample the elevation
  // grid INSIDE the polygon and use Delaunator to triangulate the
  // union of the boundary and interior points.
  //
  // bilinearElev takes (fx, fy) in [0, 1] over the polygon bbox
  // and returns the interpolated elevation; the mesh's y axis has
  // south at mnY, north at mxY (so fy=0 = south, fy=1 = north,
  // matching the backend grid's row 0 = south convention).
  const elevAtFXY = (fx, fy) => bilinearElev(elevByRC, gs, fx, fy);
  const points = [];   // array of { x, y, fx, fy, z, isBoundary }

  for (const [x, y] of polyXY) {
    const fx = (x - mnX) / spanX;
    const fy = (y - mnY) / spanY;
    const elev = elevAtFXY(fx, fy);
    points.push({ x, y, fx, fy, z: zFromElev(elev), isBoundary: true });
  }
  // Interior grid points clipped to the polygon. We use the actual
  // grid sample positions (cell-centre lat/lon mapped into mesh-XY)
  // so each surviving point carries a real elevation, not just an
  // interpolated one.
  for (let r = 0; r < gs; r++) {
    const fy = (r + 0.5) / gs;
    const y = mnY + fy * spanY;
    for (let c = 0; c < gs; c++) {
      const fx = (c + 0.5) / gs;
      const x = mnX + fx * spanX;
      const elev = elevByRC[r][c];
      if (elev == null) continue;
      if (!pointInPolygon(x, y, polyXY)) continue;
      points.push({ x, y, fx, fy, z: zFromElev(elev), isBoundary: false });
    }
  }

  const flat = new Float64Array(points.length * 2);
  for (let i = 0; i < points.length; i++) {
    flat[2 * i] = points[i].x;
    flat[2 * i + 1] = points[i].y;
  }
  const delaunay = new Delaunator(flat);
  const tris = delaunay.triangles;

  // Filter out triangles whose centroid lies outside the polygon —
  // Delaunay always covers the convex hull of its input, so concave
  // parcels would otherwise sprout fillet triangles across the
  // notches. Centroid test is robust for the small triangles a
  // grid sample produces.
  const keptTriIndices = [];   // indices into the original tris array
  for (let t = 0; t < tris.length; t += 3) {
    const a = points[tris[t]], b = points[tris[t + 1]], c = points[tris[t + 2]];
    const cx = (a.x + b.x + c.x) / 3;
    const cy = (a.y + b.y + c.y) / 3;
    if (pointInPolygon(cx, cy, polyXY)) keptTriIndices.push(t);
  }

  // ---- Skirt: 2 triangles per polygon edge ---------------------
  //
  // Bottom z=0, top z=local-elev (taken from the polygon-boundary
  // entries in `points`, indices 0..polyXY.length-1). Side walls
  // taper to a knife-edge at the lowest perimeter point —
  // geographically honest, same as before.
  const skirtCount = polyXY.length;  // closed loop
  const skirtTriCount = skirtCount * 2;
  const skirtVertexCount = skirtTriCount * 3;

  // ---- Bottom face: Delaunay-triangulated polygon at z=0 -------
  //
  // Without a bottom face the camera (DoubleSide skirt) can see
  // *into* the open prism from low angles and the inside walls
  // read as a disconnected "brown thing" floating below the
  // textured top. A flat polygon-shaped floor at z=0 occludes
  // that backface view and also gives the model proper opacity
  // when rotated.
  //
  // Triangulate the polygon outline alone (no interior points
  // — bottom doesn't need detail). Reuse Delaunator since we
  // already have it; for a convex hull of polyXY this is one
  // triangle-fan-equivalent. Centroid filter again drops any
  // hull triangles outside the polygon (concave parcels).
  const bottomFlat = new Float64Array(polyXY.length * 2);
  for (let i = 0; i < polyXY.length; i++) {
    bottomFlat[2 * i] = polyXY[i][0];
    bottomFlat[2 * i + 1] = polyXY[i][1];
  }
  const bottomD = new Delaunator(bottomFlat);
  const bottomKept = [];
  for (let t = 0; t < bottomD.triangles.length; t += 3) {
    const ia = bottomD.triangles[t], ib = bottomD.triangles[t + 1], ic = bottomD.triangles[t + 2];
    const cx = (polyXY[ia][0] + polyXY[ib][0] + polyXY[ic][0]) / 3;
    const cy = (polyXY[ia][1] + polyXY[ib][1] + polyXY[ic][1]) / 3;
    if (pointInPolygon(cx, cy, polyXY)) bottomKept.push(t);
  }
  const bottomVertexCount = bottomKept.length * 3;

  // ---- Build the combined non-indexed BufferGeometry -----------
  //
  // Layout: top vertices first (textured material slot 1), then
  // skirt + bottom vertices (vertex-coloured material slot 0).
  // Two material groups so the satellite drapes only on the top.
  const topVertexCount = keptTriIndices.length * 3;
  const totalVertexCount = topVertexCount + skirtVertexCount + bottomVertexCount;
  const positions = new Float32Array(totalVertexCount * 3);
  const colors    = new Float32Array(totalVertexCount * 3);
  const uvs       = new Float32Array(totalVertexCount * 2);

  // Top vertices
  for (let k = 0; k < keptTriIndices.length; k++) {
    const t = keptTriIndices[k];
    for (let v = 0; v < 3; v++) {
      const p = points[tris[t + v]];
      const vi = k * 3 + v;
      positions[3 * vi]     = p.x;
      positions[3 * vi + 1] = p.y;
      positions[3 * vi + 2] = p.z;
      // Natural UV mapping (matches the orientation owner verified
      // in commit 53c3c33 — "things are lined up now")
      uvs[2 * vi]     = p.fx;
      uvs[2 * vi + 1] = p.fy;
      // Per-vertex elevation colour for when wireframe is on or
      // texture is unavailable.
      const t01 = vScale > 0 ? p.z / vScale : 0;
      const col = elevColor(t01);
      colors[3 * vi]     = col.r;
      colors[3 * vi + 1] = col.g;
      colors[3 * vi + 2] = col.b;
    }
  }

  // Skirt vertices — 2 tris per edge, vertex-coloured earth tone
  const skirtR = 67 / 255, skirtG = 43 / 255, skirtB = 23 / 255;
  const skirtBase = topVertexCount;
  for (let i = 0; i < polyXY.length; i++) {
    const j = (i + 1) % polyXY.length;
    const p0 = polyXY[i], p1 = polyXY[j];
    const z0 = points[i].z, z1 = points[j].z;
    // Triangle 1: (p0, 0) → (p1, 0) → (p0, z0)
    // Triangle 2: (p1, 0) → (p1, z1) → (p0, z0)
    const verts = [
      [p0[0], p0[1], 0],
      [p1[0], p1[1], 0],
      [p0[0], p0[1], z0],
      [p1[0], p1[1], 0],
      [p1[0], p1[1], z1],
      [p0[0], p0[1], z0],
    ];
    for (let v = 0; v < 6; v++) {
      const vi = skirtBase + i * 6 + v;
      positions[3 * vi]     = verts[v][0];
      positions[3 * vi + 1] = verts[v][1];
      positions[3 * vi + 2] = verts[v][2];
      // UVs unused for the vertex-coloured skirt material; keep at 0.
      uvs[2 * vi]     = 0;
      uvs[2 * vi + 1] = 0;
      colors[3 * vi]     = skirtR;
      colors[3 * vi + 1] = skirtG;
      colors[3 * vi + 2] = skirtB;
    }
  }

  // Bottom face vertices — same dark earth as the skirt, vertex CCW
  // when viewed from below so the outward normal points down (-Z).
  const bottomR = 45 / 255, bottomG = 31 / 255, bottomB = 20 / 255;
  const bottomBase = topVertexCount + skirtVertexCount;
  for (let k = 0; k < bottomKept.length; k++) {
    const t = bottomKept[k];
    // Reverse winding (b, a, c) so the normal points downward.
    const idxs = [bottomD.triangles[t + 1], bottomD.triangles[t], bottomD.triangles[t + 2]];
    for (let v = 0; v < 3; v++) {
      const polyIdx = idxs[v];
      const vi = bottomBase + k * 3 + v;
      positions[3 * vi]     = polyXY[polyIdx][0];
      positions[3 * vi + 1] = polyXY[polyIdx][1];
      positions[3 * vi + 2] = 0;
      uvs[2 * vi]     = 0;
      uvs[2 * vi + 1] = 0;
      colors[3 * vi]     = bottomR;
      colors[3 * vi + 1] = bottomG;
      colors[3 * vi + 2] = bottomB;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color',    new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('uv',       new THREE.BufferAttribute(uvs, 2));
  geometry.clearGroups();
  geometry.addGroup(0,                                  topVertexCount,                       1);  // top → textured
  geometry.addGroup(topVertexCount,                     skirtVertexCount + bottomVertexCount, 0);  // skirt + bottom → vertex colour
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  return geometry;
}

// Build the satellite-tile URL for the parcel bbox via our lambda2
// proxy (see backend/src/landai/api/v1/satellite_proxy.py — same
// proxy PR-698 added for PropertyCard + Report). Direct hits to
// services.arcgisonline.com take 5-16s/image from slow CloudFront
// PoPs (the 3D terrain mesh fetches 100+ texture tiles per parcel,
// turning into a multi-minute wait — observed in the 2026-05-31 HAR);
// through our edge it's ~50ms with disk LRU caching.
//
// The proxy defaults to PNG; we pass &fmt=png explicitly. pxSize is
// clamped server-side to 1500 max which is plenty for the 3D mesh.
function buildSatelliteUrl(minLon, minLat, maxLon, maxLat, pxSize = 768) {
  const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  return (
    `/api/v1/satellite-preview?bbox=${bbox}&size=${pxSize},${pxSize}&fmt=png`
  );
}

// Two materials: one textured (top), one vertex-colored (sides+bottom).
// ExtrudeGeometry by default puts top+bottom in material group 0 and
// sides in group 1, so we hand it [textured, colored] and let the
// geometry pick.
function useTerrainMaterials(satelliteUrl) {
  // Two competing failure modes here. The original code called
  // loader.load() synchronously and returned the Texture object
  // before its .image had been fetched. On a cache miss the
  // material would draw a black/transparent texture for the
  // first frames (the "~1 in 5 unlit" symptom).
  //
  // The previous fix gated the texture in useState until onLoad
  // fired — that broke the material slot entirely because r3f
  // doesn't always recompile the StandardMaterial shader when
  // its `map` prop transitions from undefined → Texture (the
  // `USE_MAP` shader define stays off). Result: textures never
  // showed at all.
  //
  // Current approach: return the Texture synchronously (so
  // r3f sees a Texture object from the first render and the
  // shader compiles with USE_MAP enabled), AND explicitly bump
  // tex.needsUpdate inside the load callback so the GPU upload
  // happens once the image bytes are actually present. The
  // material's `map` reference never changes; only the texture's
  // image data does.
  const texture = useMemo(() => {
    if (!satelliteUrl) return null;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const tex = loader.load(
      satelliteUrl,
      () => {
        // Image bytes arrived — flag the texture so its current
        // GPU upload is invalidated and re-done on the next frame.
        // Three.js sets this internally on .image assignment but
        // bumping it again is harmless and defends against the
        // race observed earlier on cache misses.
        tex.needsUpdate = true;
      },
      undefined,
      (err) => {
        console.warn('terrain texture load failed:', err);
      },
    );
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = 16;
    tex.flipY = true;
    return tex;
  }, [satelliteUrl]);
  return texture;
}

function TerrainMesh({ coords2d, elevByRC, gs, eMin, eMax, vScale, satelliteTexture, wireframe, controlsRef }) {
  const meshRef = useRef();
  const { geometry, box } = useMemo(() => {
    const g = buildTerrainMesh(coords2d, elevByRC, gs, eMin, eMax, vScale);
    return { geometry: g, box: g.boundingBox.clone() };
  }, [coords2d, elevByRC, gs, eMin, eMax, vScale]);

  useFrame(({ camera, gl }, _delta, _frame) => {
    // Fit on first frame (when the ref is wired). We do this here
    // rather than in useEffect because OrbitControls' .target
    // isn't a React-tracked prop and the camera distance also
    // depends on the geometry's true bbox.
    if (meshRef.current && !meshRef.current.userData.fitted) {
      fitCamera(camera, controlsRef.current, box);
      meshRef.current.userData.fitted = true;
      // Anisotropic filtering — only available once the renderer
      // exists (i.e. inside a frame), bumps the satellite texture
      // sharpness dramatically at oblique viewing angles which is
      // exactly what 3/4 view + OrbitControls produce.
      if (satelliteTexture && gl?.capabilities) {
        const max = gl.capabilities.getMaxAnisotropy?.() || 16;
        satelliteTexture.anisotropy = max;
        satelliteTexture.needsUpdate = true;
      }
    }
  });

  // Materials by index — ExtrudeGeometry's default group layout is
  // [0]=sides, [1]=top+bottom. We want texture on top: assign
  // textured to slot 1, vertex-colored to slot 0.
  return (
    <mesh ref={meshRef} geometry={geometry} castShadow receiveShadow>
      {/* slot 0 — sides */}
      <meshStandardMaterial
        attach="material-0"
        vertexColors
        side={THREE.DoubleSide}
        flatShading
        roughness={0.85}
        metalness={0.0}
        wireframe={wireframe}
      />
      {/* slot 1 — top+bottom faces. Texture on top is the satellite
          imagery; bottom is hidden by OrbitControls' polar-angle cap. */}
      <meshStandardMaterial
        attach="material-1"
        map={wireframe ? null : (satelliteTexture || undefined)}
        // Without a texture, fall back to the same vertex-colored
        // appearance so the top still reads as terrain.
        vertexColors={wireframe || !satelliteTexture}
        side={THREE.DoubleSide}
        roughness={0.75}
        metalness={0.0}
        wireframe={wireframe}
      />
    </mesh>
  );
}

export default function ParcelTerrain3D({ geometry, elevationRange, elevationGrid }) {
  // Same null-out gates as the SVG version so we don't render an
  // empty WebGL canvas when there's no data.
  if (!geometry || elevationRange == null || elevationRange < 2) return null;
  if (!elevationGrid || !elevationGrid.cells || elevationGrid.size < 2) return null;

  let coords;
  if (geometry.type === 'Polygon') {
    coords = geometry.coordinates[0];
  } else if (geometry.type === 'MultiPolygon') {
    coords = geometry.coordinates[0][0];
  } else {
    return null;
  }
  if (!coords || coords.length < 4) return null;

  // Project polygon to a local SVG-plane: x grows east in metres,
  // y grows south in metres. Same projection as ParcelCakeView so a
  // user toggling between the two sees the same orientation.
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const latMid = (minLat + maxLat) / 2;
  const lonCos = Math.cos(latMid * Math.PI / 180);
  const wM = (maxLon - minLon) * lonCos * 111320;
  const hM = (maxLat - minLat) * 110540;
  const larger = Math.max(wM, hM) || 1;
  const SIZE = 100;  // normalised plane units — Three units, not pixels
  const nx = (wM / larger) * SIZE;
  const ny = (hM / larger) * SIZE;
  const spanLon = maxLon - minLon || 1;
  const spanLat = maxLat - minLat || 1;
  const coords2d = coords.map(([lon, lat]) => [
    ((lon - minLon) / spanLon) * nx,
    ((maxLat - lat) / spanLat) * ny,
  ]);

  const gs = elevationGrid.size;
  const elevByRC = Array.from({ length: gs }, () => Array(gs).fill(null));
  for (const c of elevationGrid.cells) {
    if (c.elevation_m != null) elevByRC[c.row][c.col] = c.elevation_m;
  }
  const eMin = elevationGrid.min_elevation_m;
  const eMax = elevationGrid.max_elevation_m;

  // Vertical exaggeration. Iteratively tuned by eye: 4× was still
  // hyper-exaggerated, 2× of the original baseline reads as
  // believable terrain. A 200m+ ridge reaches ~36 units against a
  // SIZE=100 horizontal base (~36% aspect — clearly 3D but not
  // mountainous). Sqrt easing unchanged so 5–20m ranges stay
  // perceptible.
  const REF_RANGE_M = 200;
  const VSCALE_MIN = 8;
  const VSCALE_MAX = 36;
  const ratio = Math.min(1, Math.sqrt(Math.max(0, elevationRange) / REF_RANGE_M));
  const vScale = VSCALE_MIN + (VSCALE_MAX - VSCALE_MIN) * ratio;

  // Satellite imagery URL covering the parcel bbox — single Esri
  // World Imagery export. ~768x768 ≈ 1m/pixel for a 700m parcel,
  // 4m/pixel for a 3km ranch — plenty given a 280px-tall canvas at
  // 2x DPR. Free service, no API key.
  const satelliteUrl = useMemo(
    () => buildSatelliteUrl(minLon, minLat, maxLon, maxLat, 768),
    [minLon, minLat, maxLon, maxLat],
  );
  const satelliteTexture = useTerrainMaterials(satelliteUrl);

  const controlsRef = useRef();
  // Debug toggle (#141): show every triangle edge so you can read the
  // grid resolution visually. Default OFF (satellite texture wins);
  // flip ON in the panel header when you need to read mesh density.
  const [wireframe, setWireframe] = useState(false);

  return (
    <div style={{
      padding: '12px 14px', borderRadius: 8, marginBottom: 10,
      background: 'linear-gradient(135deg, #1a1a2e10, #1a1a2e06)',
      border: '1px solid #1a1a2e30',
    }} data-testid="parcel-terrain-3d">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#4a5568', fontWeight: 700, letterSpacing: 0.5 }}>
          3D Terrain View
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.66rem', color: '#888' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={wireframe}
              onChange={(e) => setWireframe(e.target.checked)}
              style={{ margin: 0 }}
            />
            wireframe
          </label>
          <span>drag to rotate · scroll to zoom</span>
        </div>
      </div>
      {/* Container is intentionally lighter than the surrounding page
          (#1a1a2e) — the page tone made the unlit-mesh moments read as
          pure black-on-blue. A softer 2a2c3e gives the mesh visual
          breathing room without breaking the dark theme. */}
      <div style={{ width: '100%', height: 280, borderRadius: 4, overflow: 'hidden', background: '#2a2c3e' }}>
        <Canvas
          camera={{ position: [80, -110, 90], up: [0, 0, 1], fov: 35 }}
          dpr={[1, 2]}
          shadows
        >
          {/* Z-up world: lights positioned in the world frame.
              Directional light comes from SE at elevation (high
              positive Z) so foreground slopes catch warm light and
              N-facing slopes pick up the cool hemisphere fill.
              Ambient bumped to 0.6 + directional to 1.05 so the
              vertex-color fallback (terrain-tint shading when the
              satellite texture is still loading or failed) reads
              clearly against the dark container. */}
          <ambientLight intensity={0.6} />
          <directionalLight
            position={[60, -40, 100]}
            intensity={1.05}
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
          />
          <hemisphereLight args={['#d4d4ff', '#3a2a1f', 0.4]} />
          <TerrainMesh
            coords2d={coords2d}
            elevByRC={elevByRC}
            gs={gs}
            eMin={eMin}
            eMax={eMax}
            vScale={vScale}
            satelliteTexture={satelliteTexture}
            wireframe={wireframe}
            controlsRef={controlsRef}
          />
          <OrbitControls
            ref={controlsRef}
            enableDamping
            dampingFactor={0.08}
            minDistance={20}
            maxDistance={400}
            // With camera.up = +Z, OrbitControls' polar angle is
            // measured from the +Z (zenith) axis. Allowing the user
            // to swing slightly past the equator (PI * 0.55) lets
            // them peek at the mesh from just-above-horizon without
            // clipping through the ground.
            maxPolarAngle={Math.PI * 0.55}
            onChange={(e) => {
              if (e?.target) {
                if (!e.target.userData) e.target.userData = {};
                e.target.userData.interacted = true;
              }
            }}
          />
          <AutoSpin controlsRef={controlsRef} until={Date.now() + 12000} />
        </Canvas>
      </div>
      <div style={{ fontSize: '0.7rem', color: '#888', textAlign: 'center', marginTop: 4 }}>
        Relief: {Math.round(elevationRange * 3.28084)} ft vertical · textured from DEM
      </div>
    </div>
  );
}
